/**
 * MapBoundary's state machine — pure, so it is tested without a DOM (#7696).
 *
 * A boundary is in exactly one of three phases, and the third is the one this
 * module exists for:
 *
 *   * `placeholder` — the request-free stand-in (D13), before the map is wanted.
 *   * `live`        — the Maps JS SDK is mounted (or loading).
 *   * `unavailable` — the SDK could NOT LOAD: the browser never received
 *     `maps.googleapis.com` (mainland China, a blocking network or extension,
 *     a crawler with third-party fetches off). The relay census measured this
 *     on ~13% of estate views and 21% on zh-CN, and until this phase existed
 *     the page showed an EMPTY 380 px box for as long as the reader stayed —
 *     the live map's container, with nothing ever painted into it.
 *
 * `unavailable` is TERMINAL: a script that failed to load does not load on a
 * second mount (the loader memoises its failure), so nothing transitions out
 * of it — not a tap, not scrolling back into view — and the boundary renders
 * its Google-free fallback for the rest of the page's life. A TIMED-OUT load
 * is terminal by the same rule, even though the hung request might still land
 * later: a box that swaps fallback → map under the reader's eyes, after they
 * have read the address, is worse than a fallback that stays.
 *
 * It is a VARIANT, never a boolean beside the others: "live AND failed" is not
 * a state the component can hold.
 *
 * Two transitions enter it (#8204): the script ERRORED (`<APIProvider
 * onError>`), or it neither loaded nor errored within
 * `MAPS_SCRIPT_LOAD_DEADLINE_MS` — a blackholed connection never fires
 * `error`, and before the deadline such a page kept the empty box forever.
 */
import type { WebMapsDegradedReason } from '@/shared/lib/metrics/client-metrics_gen';

/**
 * Why a map is unavailable — the subset of the generated `web_maps_degraded`
 * reasons that leaves NO map to show: the `script_load_*` family, DERIVED by
 * its prefix from the vocabulary (`web/manifests/metrics.yaml`), never listed
 * by hand. `auth_failure` / `dev_watermark` still paint a (degraded) map, so
 * they are reported, not rendered around. A new `script_load_*` value widens
 * this type, and {@link UNAVAILABLE_CAUSE_OF}'s test then fails to type-check
 * until a transition enters it.
 */
export type MapUnavailableCause = Extract<WebMapsDegradedReason, `script_load_${string}`>;

/** The SDK could not load; the boundary renders its Google-free fallback. */
export interface MapUnavailable {
  readonly kind: 'unavailable';
  readonly cause: MapUnavailableCause;
}

/** The boundary's phase. */
export type MapPhase =
  | { readonly kind: 'placeholder' }
  | { readonly kind: 'live' }
  | MapUnavailable;

/** The events that end the SDK load without a map. */
export type ScriptLoadEndedEvent =
  /** `<APIProvider onError>`: the SDK script did not load. */
  | { readonly kind: 'script_load_failed' }
  /** The SDK neither loaded nor errored within `MAPS_SCRIPT_LOAD_DEADLINE_MS`. */
  | { readonly kind: 'script_load_timed_out' };

/** What can happen to a boundary. */
export type MapPhaseEvent =
  /** The reader asked for the map (`load="tap"`'s button). */
  { readonly kind: 'requested' } | ScriptLoadEndedEvent;

/**
 * Which cause each script-load event records — one row per event, and (held
 * by `map-phase.test.ts`'s type assertion) one event per cause.
 */
export const UNAVAILABLE_CAUSE_OF = {
  script_load_failed: 'script_load_error',
  script_load_timed_out: 'script_load_timeout',
} as const satisfies Record<ScriptLoadEndedEvent['kind'], MapUnavailableCause>;

export const INITIAL_MAP_PHASE: MapPhase = { kind: 'placeholder' };

/** The transition function (a `useReducer` reducer). Total and pure. */
export function nextMapPhase(phase: MapPhase, event: MapPhaseEvent): MapPhase {
  if (phase.kind === 'unavailable') return phase;
  if (event.kind === 'requested') return phase.kind === 'live' ? phase : { kind: 'live' };
  return { kind: 'unavailable', cause: UNAVAILABLE_CAUSE_OF[event.kind] };
}

/** What the boundary renders. */
export type MapView = MapPhase['kind'];

/**
 * Resolve the RENDERED view from the held phase and the two inputs that are
 * not state: an `in-view` boundary's "seen" (a hook value), and the caller's
 * `defer`. `unavailable` wins over both — a failed SDK is not waiting on
 * anything — and `defer` holds the placeholder otherwise.
 */
export function resolveMapView(
  phase: MapPhase,
  inputs: { readonly seenInView: boolean; readonly defer: boolean }
): MapView {
  if (phase.kind === 'unavailable') return 'unavailable';
  if (inputs.defer) return 'placeholder';
  return phase.kind === 'live' || inputs.seenInView ? 'live' : 'placeholder';
}

/**
 * The "open in maps" link for a coordinate. A LINK, not a load: the failure is
 * that the browser cannot fetch Google's SCRIPT, and a link costs the page
 * nothing — the reader's own maps app or browser decides whether it can reach
 * it. Google's documented, keyless Maps URL (`api=1`), which every platform's
 * maps handler accepts.
 */
export function openInMapsHref(pin: { readonly lat: number; readonly lng: number }): string {
  return `https://www.google.com/maps/search/?api=1&query=${pin.lat},${pin.lng}`;
}
